'use client';

import { useCallback } from 'react';

import { REFERRER_TYPE_MAP } from '@workspace/lib/constants/analytics';
import { IS_NOT_EQUAL } from '@workspace/lib/constants/custom-validators';
import { useReadFullPath } from '@workspace/lib/hooks/analytics/use-read-full-path';
import { useUserSessionData } from '@workspace/lib/hooks/analytics/use-user-session-data';
import {
  SESSION_STORE_KEYS,
  useSessionStore,
} from '@workspace/lib/hooks/store/use-session-store';
import type {
  AnalyticsEventType,
  ApiAnalyticsData,
  EndpointSpecificAnalytics,
} from '@workspace/lib/types/analytics';
import { filterObject } from '@workspace/lib/utils/filter-object';
import { getDeviceInfoForAnalyticsApi } from '@workspace/lib/utils/get-device-info';
import { cleanUtmParams, getStoredUtmParams } from '@workspace/lib/utils/utm';

const UTM_FILTER_CONDITION = `${IS_NOT_EQUAL}id`;

export const useRestApiAnalyticsData = () => {
  const { getFromSessionStore } = useSessionStore();
  const currentFullPath = useReadFullPath();
  const {
    getDaysSinceLastVisit,
    getVisitLengthSeconds,
    getNumberOfVisits,
    getUserIp,
  } = useUserSessionData();

  const composeAnalytics = useCallback(
    (
      analytics?: EndpointSpecificAnalytics,
      isCollectIp = true,
    ): ApiAnalyticsData => {
      const {
        userEmail = '',
        event = { eventType: '' as AnalyticsEventType, eventName: '' },
      } = analytics || {};

      const device = getDeviceInfoForAnalyticsApi();
      const session: ApiAnalyticsData['session'] = {
        numVisits: getNumberOfVisits(),
        visitLengthSeconds: getVisitLengthSeconds(),
        daysSinceLastVisit: getDaysSinceLastVisit(),
      };

      if (isCollectIp) session.ip = getUserIp();

      const previousUrl =
        (getFromSessionStore(SESSION_STORE_KEYS.previousPath) as string) ?? '';

      const referrerUrl =
        typeof document !== 'undefined' ? document.referrer : '';

      const attributionData = getStoredUtmParams();

      const cleanedUtmParams = cleanUtmParams(attributionData?.utms ?? {});
      const keyword = cleanedUtmParams?.term ?? '';
      const trafficType = attributionData?.trafficType ?? '';
      const referrerType = REFERRER_TYPE_MAP[trafficType] ?? 'other';

      return {
        search: { keyword, queryString: window.location.search },
        userEmail: userEmail ?? '',
        url: {
          currentUrl: currentFullPath,
          previousUrl,
        },
        event,
        device,
        referrer: {
          url: referrerUrl,
          type: referrerType,
          trafficType,
          raw: referrerUrl,
        },
        session,
        ...(cleanedUtmParams && {
          utm:
            'id' in cleanedUtmParams
              ? filterObject(cleanedUtmParams, UTM_FILTER_CONDITION)
              : cleanedUtmParams,
        }),
      };
    },
    [
      currentFullPath,
      getFromSessionStore,
      getNumberOfVisits,
      getUserIp,
      getVisitLengthSeconds,
      getDaysSinceLastVisit,
    ],
  );

  return { composeAnalytics };
};
